import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import HomeBtn from "./Instruments/HomeBtn";
import { getDecoderOwner, createPayment } from "../../../shared/service";

// Payment provider options (see POST /payments/pay contract).
const PROVIDERS = [
  { value: "OM", label: "Orange money" },
  { value: "MTN", label: "MTN mobile money" },
];

// Amount for the API: float with two decimals (e.g. 123.50), matching the contract.
const toAmountFloat = (value) => parseFloat(Number(value).toFixed(2));

/**
 * Public "pay by decoder id" flow (no authentication):
 *   1. GET /api/user/decoder/{id} -> { result, user_id, phone }
 *      - user_id == null  -> decoder not registered -> go to the add-decoder flow
 *      - otherwise         -> show the payment form (phone preloaded)
 *   2. POST /payments/pay { user_id, phone, provider, amount }
 *
 * Reached at /pay (optionally with ?dcr_id=37-511893 to auto-look-up).
 */
const PayByDecoder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 'lookup' = enter/confirm decoder id, 'pay' = payment form, 'success' = done
  const [step, setStep] = useState("lookup");
  const [decoderId, setDecoderId] = useState(searchParams.get("dcr_id") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // owner info resolved from the decoder lookup
  const [userId, setUserId] = useState(null);

  // payment form state
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState("");
  const [provider, setProvider] = useState("OM");
  const [amountError, setAmountError] = useState("");

  const lookupDecoder = useCallback(async (id) => {
    const trimmed = (id || "").trim();
    if (!trimmed) {
      setError("Enter your decoder id");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const data = await getDecoderOwner(trimmed);
      if (data?.result !== "OK") {
        setError(data?.result || "Decoder not found");
        return;
      }
      if (data.user_id == null) {
        // Decoder has no owner yet -> reuse the existing add-decoder deep link
        // (full navigation so the router's add-dcr handler runs on load).
        window.location.href = `/add-dcr?dcr_id=${encodeURIComponent(trimmed)}`;
        return;
      }
      setUserId(data.user_id);
      setPhone(data.phone || "");
      setStep("pay");
    } catch (e) {
      console.error("Decoder lookup failed:", e);
      setError("Decoder not found or service unavailable");
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-look-up when arriving with ?dcr_id=...
  useEffect(() => {
    const fromUrl = searchParams.get("dcr_id");
    if (fromUrl) lookupDecoder(fromUrl);
  }, [searchParams, lookupDecoder]);

  const handlePay = async () => {
    const amountFloat = toAmountFloat(amount);
    if (Number.isNaN(amountFloat) || amountFloat <= 0) {
      setAmountError("Enter a valid amount");
      return;
    }
    setAmountError("");
    setError("");
    setLoading(true);
    try {
      const response = await createPayment({
        user_id: userId,
        phone: phone || undefined,
        provider,
        amount: amountFloat,
      });
      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }
      if (!response.ok || (data && data.result !== "OK")) {
        setError(
          (data && data.result) || `Payment failed (${response.status})`,
        );
        return;
      }
      setStep("success");
    } catch (e) {
      console.error("Payment failed:", e);
      setError("Network error, please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="main for-log">
        <HomeBtn />
        <div className="log">
          <div className="logs">
            {step === "lookup" && (
              <>
                <h2 className="main-pages-titles">Pay by decoder</h2>
                <p className="inputs">
                  <input
                    type="text"
                    autoFocus
                    className="form"
                    placeholder="Decoder id (e.g. 37-511893)"
                    value={decoderId}
                    onChange={(e) => setDecoderId(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") lookupDecoder(decoderId);
                    }}
                  />
                </p>
                {error && <p className="error-message">{error}</p>}
                <div className="log-container">
                  <button
                    className="grid-log login-accept-btn"
                    disabled={loading}
                    onClick={() => lookupDecoder(decoderId)}
                  >
                    {loading ? "..." : "Continue"}
                  </button>
                  <button
                    className="grid-log register-route-btn"
                    onClick={() => navigate("/")}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            {step === "pay" && (
              <>
                <h2 className="main-pages-titles">Make payment</h2>
                <p className="inputs">
                  <input
                    type="number"
                    className="form"
                    placeholder="Amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </p>
                {amountError && <p className="error-message">{amountError}</p>}
                <p className="inputs">
                  <input
                    type="text"
                    className="form"
                    placeholder="Phone number"
                    value={phone}
                    readOnly
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </p>
                <p className="inputs">
                  <select
                    className="form"
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                  >
                    {PROVIDERS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </p>
                {error && <p className="error-message">{error}</p>}
                <div className="log-container">
                  <button
                    className="grid-log login-accept-btn"
                    disabled={loading}
                    onClick={handlePay}
                  >
                    {loading ? "..." : "Confirm"}
                  </button>
                  <button
                    className="grid-log register-route-btn"
                    onClick={() => setStep("lookup")}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            {step === "success" && (
              <>
                <h2 className="main-pages-titles">Payment requested</h2>
                <p>Your payment request has been sent.</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayByDecoder;
